/* Collection/search controls. Native GET remains the no-JS fallback. */
(() => {
  if (customElements.get('facet-controller')) return;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const densityKey = 'product-grid-view';
  const storedDensity = () => {
    try { return sessionStorage.getItem(densityKey) === 'zoom-out' ? 'dense' : 'default'; }
    catch { return 'default'; }
  };

  // The native disclosure remains the no-JS and final-state contract. Its
  // body is measured each time, including a reversal in the middle of motion.
  function enhanceDisclosure(details) {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .facet__panel');
    if (!summary || !content) return null;
    const body = document.createElement('div');
    body.className = 'facet__motion-body';
    content.before(body); body.append(content);
    const state = { expanded: details.open, animation: null, target: 0 };
    const settle = () => {
      state.animation?.cancel(); state.animation = null;
      details.open = state.expanded;
      body.style.removeProperty('overflow');
      details.dataset.facetExpanded = String(state.expanded);
    };
    const transition = expanded => {
      const height = details.open ? body.getBoundingClientRect().height : 0;
      const opacity = details.open ? Number(getComputedStyle(body).opacity) : 0;
      state.animation?.cancel(); state.animation = null;
      state.expanded = expanded;
      details.dataset.facetExpanded = String(expanded);
      if (reduceMotion() || !body.animate) { settle(); return; }
      details.open = true;
      state.target = expanded ? content.getBoundingClientRect().height : 0;
      body.style.overflow = 'hidden';
      const animation = body.animate([{ height: `${height}px`, opacity }, { height: `${state.target}px`, opacity: expanded ? 1 : 0 }], {
        duration: 280, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'
      });
      state.animation = animation;
      animation.onfinish = () => { if (state.animation === animation) settle(); };
    };
    const click = event => {
      if (event.defaultPrevented || event.button > 0 || event.target.closest('a,button,input,select,textarea')) return;
      event.preventDefault(); transition(!state.expanded);
    };
    const toggle = () => {
      if (state.animation) { if (!details.open) { state.expanded = false; settle(); } return; }
      state.expanded = details.open; details.dataset.facetExpanded = String(details.open);
    };
    const observer = new ResizeObserver(() => {
      if (state.animation && state.expanded && Math.abs(content.getBoundingClientRect().height - state.target) > .5) transition(true);
    });
    observer.observe(content);
    summary.addEventListener('click', click); details.addEventListener('toggle', toggle);
    details.dataset.facetExpanded = String(details.open);
    return {
      settle,
      destroy() {
        settle(); observer.disconnect(); summary.removeEventListener('click', click); details.removeEventListener('toggle', toggle);
        body.before(content); body.remove(); delete details.dataset.facetExpanded;
      }
    };
  }
  /* Collection filtering keeps a native GET form and progressively enhances it.
     The mobile drawer contains that same form; no duplicated inputs or IDs. */
  class FacetController extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.panel = this.querySelector('.facet-filtering');
      this.form = this.querySelector('[data-facet-form]');
      if (!this.form || !this.panel) return;
      this.initialized = true;
      this.toggle = this.querySelector('[data-facet-toggle]');
      this.dialog = this.querySelector('.facet-drawer');
      this.host = this.dialog?.closest('dialog-component');
      this.anchor = document.createComment('facet panel desktop position');
      this.panel.before(this.anchor);
      this.sidebarOpen = this._restoredState?.sidebarOpen ?? true;
      this.density = this._restoredState?.density ?? storedDensity();
      this.valueAnimations = new Map();
      this.revealAnimations = [];
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', this.onMotionPreference);
      this.requestId = 0;
      this.mobile = window.matchMedia('(max-width: 1023px)');
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('formdata', this.onFormData);
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.dialog?.addEventListener('cancel', this.onDialogDismiss, true);
      this.dialog?.addEventListener('click', this.onDialogDismiss, true);
      this.querySelectorAll('[data-price-bound]').forEach((input) => { input.type = 'text'; });
      window.addEventListener('resize', this.layout);
      window.addEventListener('popstate', this.onPopState);
      this.classList.add('facet-controller--ready');
      this.querySelectorAll('[data-price-facet]').forEach((price) => this.updatePrice(price));
      this.querySelectorAll('.facet').forEach((group) => {
        const saved = this._restoredState?.groups?.find((item) => item.key === group.dataset.facetKey);
        if (saved) group.open = saved.open;
        const activeExtra = group.querySelector('.facet__item--extra input:checked');
        if (saved?.expanded || activeExtra) this.expandValues(group, true);
      });
      this.disclosures = [...this.querySelectorAll('details.facet')].map(enhanceDisclosure).filter(Boolean);
      this.layout();
      // Nested custom elements finish connecting after their parent. Restore a
      // replaced drawer only after the existing dialog host has initialized.
      queueMicrotask(() => {
        if (!this.isConnected) return;
        if (this._restoredState?.drawerOpen && this.mobile.matches) {
          this.host?.showDialog(this.toggle);
          this.toggle?.setAttribute('aria-expanded', 'true');
        }
        const focus = this._restoredState?.focus;
        if (focus) {
          const target = [...this.querySelectorAll('input, select, button, a')].find((el) =>
            (focus.id && el.id === focus.id) || (focus.name && el.name === focus.name && el.value === focus.value));
          target?.focus({ preventScroll: true });
        }
      });
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      this.requestId += 1;
      this.pending?.abort();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('formdata', this.onFormData);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.dialog?.removeEventListener('cancel', this.onDialogDismiss, true);
      this.dialog?.removeEventListener('click', this.onDialogDismiss, true);
      clearTimeout(this.closeTimer);
      window.removeEventListener('resize', this.layout);
      window.removeEventListener('popstate', this.onPopState);
      this.host?.close?.();
      this.onMotionPreference();
      this.disclosures?.forEach((disclosure) => disclosure.destroy());
      this.reducedMotion?.removeEventListener('change', this.onMotionPreference);
      this.anchor?.remove();
    }

    layout = () => {
      if (!this.isConnected) return;
      if (this.layoutWidth !== undefined && this.layoutWidth !== window.innerWidth) this.onMotionPreference();
      this.layoutWidth = window.innerWidth;
      const mobile = this.mobile.matches;
      const body = this.querySelector('[data-facet-drawer-body]');
      if (mobile && body && this.panel.parentNode !== body) body.append(this.panel);
      if (!mobile && this.panel.previousSibling !== this.anchor) this.anchor.after(this.panel);
      if (!mobile) this.host?.close?.();
      this.panel.hidden = mobile ? false : (!this.sidebarOpen || this.dataset.filterEnabled === 'false');
      this.querySelectorAll('[data-facet-mobile-sort]').forEach((input) => { input.disabled = !mobile; });
      const select = this.querySelector('[data-facet-sort]');
      if (select) select.disabled = mobile;
      this.toggle?.setAttribute('aria-controls', mobile ? this.dialog.id : this.panel.id);
      this.toggle?.setAttribute('aria-expanded', String(mobile ? !!this.dialog?.open : !this.panel.hidden));
      if (mobile) this.toggle?.setAttribute('aria-haspopup', 'dialog');
      else this.toggle?.removeAttribute('aria-haspopup');
      this.updateGrid();
    };

    updateGrid() {
      const results = this.querySelector('[data-results]');
      if (!results) return;
      const base = Number(this.dataset.baseColumns) || 4;
      const dense = this.density === 'dense' ? 1 : 0;
      this.dataset.gridDensity = this.density;
      const sidebar = !this.mobile.matches && !this.panel.hidden ? 1 : 0;
      const narrowDesktop = window.innerWidth >= 1024 && window.innerWidth < 1200 ? 1 : 0;
      const columns = this.mobile.matches ? (window.innerWidth < 768 ? 2 : Math.max(2, base - 1)) : Math.max(1, base - sidebar - narrowDesktop + dense);
      results.style.setProperty('--grid-columns', String(columns));
      this.querySelectorAll('[data-grid-density]').forEach((button) => {
        const active = button.dataset.gridDensity === this.density;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    onDialogClose = () => {
      clearTimeout(this.closeTimer);
      this.dialog?.classList.remove('facet-drawer--closing');
      this.toggle?.setAttribute('aria-expanded', 'false');
      if (this.isConnected && this._restoredState?.scrollAfterClose) {
        this._restoredState.scrollAfterClose = false;
        this.scrollResults();
      }
    };
    onDialogDismiss = (event) => {
      if (event.type !== 'cancel' && !event.target.closest('[data-dialog-close]') && event.target !== this.dialog) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.dialog.classList.contains('facet-drawer--closing')) return;
      if (reduceMotion()) { this.host?.close(); return; }
      this.dialog.classList.add('facet-drawer--closing');
      this.closeTimer = setTimeout(() => { this.host?.close(); }, 500);
    };
    scrollResults() {
      const headerHeight = document.querySelector('.header')?.getBoundingClientRect().height || 0;
      window.scrollTo({ top: Math.max(0, window.scrollY + this.getBoundingClientRect().top - headerHeight), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }
    onPopState = () => { this.renderURL(new URL(window.location.href), false); };

    onMotionPreference = () => {
      this.disclosures?.forEach((disclosure) => disclosure.settle());
      for (const state of this.valueAnimations?.values() || []) state.finish();
      this.revealAnimations?.forEach((animation) => animation.cancel());
      this.revealAnimations = [];
    };

    revealResults() {
      this.revealAnimations.forEach((animation) => animation.cancel());
      this.revealAnimations = [];
      if (reduceMotion()) return;
      // Explicit user preference: a minimal reveal after instantaneous layout
      // changes. Reference density/filter layout itself does not animate.
      const nodes = [this.querySelector('[data-results] .product-grid'), !this.panel.hidden && this.panel].filter(Boolean);
      this.revealAnimations = nodes.map((node) => node.animate([{ opacity: .65 }, { opacity: 1 }], { duration: 150, easing: 'ease-in-out' }));
    }

    expandValues(group, expanded, animate = false) {
      if (!group) return;
      const list = group.querySelector('.facet__list');
      const from = list?.getBoundingClientRect().height;
      this.valueAnimations?.get(group)?.finish();
      group?.classList.toggle('facet--expanded', expanded);
      group?.querySelector('[data-facet-more]')?.setAttribute('aria-expanded', String(expanded));
      if (!list || !animate || reduceMotion() || !list.animate) return;
      const target = list.getBoundingClientRect().height;
      // Keep closing rows in flow until the height animation finishes.
      group.classList.toggle('facet--values-closing', !expanded);
      const extras = [...group.querySelectorAll('.facet__item--extra')];
      extras.forEach((item) => { item.inert = !expanded; });
      list.style.overflow = 'hidden';
      const animation = list.animate([{ height: `${from}px` }, { height: `${target}px` }], { duration: 300, easing: 'ease-in-out', fill: 'both' });
      const state = { finish: () => {
        animation.cancel(); list.style.removeProperty('overflow');
        group.classList.remove('facet--values-closing');
        extras.forEach((item) => { item.inert = false; });
        this.valueAnimations.delete(group);
      } };
      this.valueAnimations.set(group, state);
      animation.onfinish = () => { if (this.valueAnimations.get(group) === state) state.finish(); };
    }

    onClick = (event) => {
      const toggle = event.target.closest('[data-facet-toggle]');
      if (toggle) {
        if (this.mobile.matches) {
          this.host?.showDialog(toggle);
          toggle.setAttribute('aria-expanded', 'true');
        } else {
          this.sidebarOpen = !this.sidebarOpen;
          this.layout();
          this.revealResults();
        }
        return;
      }
      const more = event.target.closest('[data-facet-more]');
      if (more) { this.expandValues(more.closest('.facet'), more.getAttribute('aria-expanded') !== 'true', true); return; }
      const density = event.target.closest('[data-grid-density]');
      if (density) {
        if (density.dataset.gridDensity === this.density) return;
        this.density = density.dataset.gridDensity;
        try { sessionStorage.setItem(densityKey, this.density === 'dense' ? 'zoom-out' : 'default'); } catch { /* Storage can be unavailable in private contexts. */ }
        this.updateGrid(); this.revealResults(); return;
      }
      const link = event.target.closest('.facet-chip, .pagination a');
      if (link && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !link.download) {
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin) return;
        event.preventDefault();
        this.renderURL(this.withPreview(url));
      }
    };

    onInput = (event) => {
      if (event.target.matches('[data-price-bound]')) event.target.setCustomValidity('');
      const range = event.target.closest('[data-price-range]');
      if (!range) return;
      const price = range.closest('[data-price-facet]');
      const min = price.querySelector('[data-price-range="min"]');
      const max = price.querySelector('[data-price-range="max"]');
      const ceiling = Number(price.dataset.rangeMax);
      const value = range.dataset.priceRange === 'min'
        ? Math.min(Number(range.value), Math.max(0, Number(max.value) - 1))
        : Math.max(Number(range.value), Math.min(ceiling, Number(min.value) + 1));
      range.value = String(value);
      price.querySelector(`[data-price-bound="${range.dataset.priceRange}"]`).value = (value / 100).toFixed(2);
      this.updatePrice(price);
    };

    // Accept decimal commas as well as dots, including a pasted grouped amount.
    priceNumber(value) {
      let text = value.trim().replace(/[\s'’]/g, '');
      if (!text) return null;
      if (!/^-?[\d.,]+$/.test(text)) return NaN;
      if (text.includes(',') && text.includes('.')) {
        const decimal = text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.';
        text = text.replace(decimal === ',' ? /\./g : /,/g, '').replace(',', '.');
      } else text = text.replace(',', '.');
      return /^-?\d*\.?\d+$/.test(text) ? Number(text) : NaN;
    }

    updatePrice(price, changed) {
      const min = price.querySelector('[data-price-bound="min"]');
      const max = price.querySelector('[data-price-bound="max"]');
      const ceiling = Number(price.dataset.rangeMax) || 0;
      let low = this.priceNumber(min.value), high = this.priceNumber(max.value);
      for (const [input, value] of [[min, low], [max, high]]) {
        input.setCustomValidity(Number.isNaN(value) ? (price.dataset.invalidPrice || 'Enter a valid price.') : '');
      }
      if (Number.isNaN(low) || Number.isNaN(high)) return false;
      low = low === null ? null : Math.max(0, Math.min(ceiling, Math.round(low * 100)));
      high = high === null ? null : Math.max(0, Math.min(ceiling, Math.round(high * 100)));
      if (low !== null && high !== null && low > high) {
        if (changed === 'min') low = high;
        else high = low;
      }
      if (changed) {
        min.value = low === null ? '' : (low / 100).toFixed(2);
        max.value = high === null ? '' : (high / 100).toFixed(2);
      }
      price.querySelector('[data-price-range="min"]').value = low ?? 0;
      price.querySelector('[data-price-range="max"]').value = high ?? ceiling;
      price.querySelectorAll('[data-price-range]').forEach((input) => { input.disabled = ceiling <= 0; });
      price.classList.toggle('price-facet--min-front', ceiling > 0 && (low ?? 0) >= ceiling * .95);
      price.style.setProperty('--range-min', `${ceiling ? (low ?? 0) / ceiling * 100 : 0}%`);
      price.style.setProperty('--range-max', `${ceiling ? (high ?? ceiling) / ceiling * 100 : 100}%`);
      return true;
    }

    onChange = (event) => {
      const input = event.target;
      if (!input.matches('[data-facet-sort], [data-facet-mobile-sort], [data-facet-form] input')) return;
      if (input.matches('[data-facet-sort], [data-facet-mobile-sort]')) {
        const value = input.value;
        const select = this.querySelector('[data-facet-sort]');
        if (select) select.value = value;
        this.querySelectorAll('[data-facet-mobile-sort]').forEach((radio) => { radio.checked = radio.value === value; });
      }
      const price = input.closest('[data-price-facet]');
      if (price && !this.updatePrice(price, input.dataset.priceBound || input.dataset.priceRange)) { input.reportValidity(); return; }
      this.form.requestSubmit();
    };

    onFormData = (event) => {
      if (event.target !== this.form) return;
      this.form.querySelectorAll('[data-price-bound]').forEach((input) => {
        if (!input.value.trim()) event.formData.delete(input.name);
        else event.formData.set(input.name, String(this.priceNumber(input.value)));
      });
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) event.formData.set('preview_theme_id', preview);
    };

    onSubmit = (event) => {
      if (event.target !== this.form) return;
      event.preventDefault();
      const valid = [...this.querySelectorAll('[data-price-facet]')].map((price) => this.updatePrice(price, 'submit')).every(Boolean);
      if (!valid || !this.form.reportValidity()) return;
      const url = new URL(this.form.action, location.href);
      url.search = new URLSearchParams(new FormData(this.form)).toString();
      url.searchParams.delete('page');
      this.renderURL(url);
    };

    withPreview(url) {
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async renderURL(url, push = true) {
      if (url.origin !== location.origin || !this.dataset.sectionId) { location.assign(url.href); return; }
      this.pending?.abort();
      const abort = new AbortController();
      this.pending = abort;
      const requestId = ++this.requestId;
      this.setAttribute('aria-busy', 'true');
      const fetchURL = new URL(url.href);
      fetchURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(fetchURL, { signal: abort.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Section request failed');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = [...documentFragment.querySelectorAll('facet-controller')].find((node) => node.dataset.sectionId === this.dataset.sectionId);
        if (!replacement?.querySelector('[data-results]')) throw new Error('Missing results section');
        if (requestId !== this.requestId || !this.isConnected) return;
        const active = document.activeElement;
        replacement._restoredState = {
          sidebarOpen: this.sidebarOpen, density: this.density, drawerOpen: !!this.dialog?.open, scrollAfterClose: !!this.dialog?.open,
          groups: [...this.querySelectorAll('.facet')].map((group) => ({ key: group.dataset.facetKey, open: group.open, expanded: group.classList.contains('facet--expanded') })),
          focus: this.contains(active) ? { id: active.id, name: active.name, value: active.value } : null,
        };
        if (push && url.href !== location.href) history.pushState({}, '', url.href);
        const keepDrawer = replacement._restoredState.drawerOpen;
        this.replaceWith(replacement);
        document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: replacement.dataset.sectionId } }));
        if (!keepDrawer) {
          replacement.scrollResults();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.requestId && this.isConnected) location.assign(url.href);
      } finally {
        if (requestId === this.requestId && this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('facet-controller')) customElements.define('facet-controller', FacetController);

  const inSection = root => [
    ...(root.matches?.('facet-controller') ? [root] : []), ...root.querySelectorAll('facet-controller')
  ];
  document.addEventListener('shopify:section:unload', event => inSection(event.target).forEach(controller => controller.disconnectedCallback()));
  document.addEventListener('shopify:section:load', event => inSection(event.target).forEach(controller => controller.connectedCallback()));

})();
